import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import useApiClient from 'hooks/use-api-client';
import { useCallback } from 'react';
import type { BillingApi } from '@env0/billing-service/api';
import { useFeatureFlag } from 'hooks/use-feature-flags';
import { FeatureFlag } from 'constants/feature-flags';

const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();
  const baseKey = [orgId, 'billing-information'] as const;
  return { base: baseKey, usage: [orgId, 'billing-usage'] as const, invoices: [orgId, 'billing-invoices'] as const };
};

export const useGetBillingInformation = () => {
  const apiClient = useApiClient();
  const { base } = useCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: base,
    queryFn: async ({ queryKey: [orgId] }) => {
      try {
        return await apiClient.billing.getBillingInformation(orgId);
      } catch (error) {
        // A missing row means the free tier: every organization is given one on creation. Any other
        // failure is surfaced — swallowing it told a paying customer they were free (ATH-474).
        if ((error as AxiosError).response?.status !== StatusCodes.NOT_FOUND) throw error;

        return { tier: 'basic' } as BillingApi.BillingInfo;
      }
    },
    staleTime: Infinity,
    enabled: !!orgId
  });
};

export const useGetOrganizationUsage = () => {
  const apiClient = useApiClient();
  const { usage } = useCacheKeys();
  const orgId = useCurrentOrganizationId();
  const dailyHistory = useFeatureFlag(FeatureFlag.BILLING_USAGE_DAILY_HISTORY);

  return useQuery({
    queryKey: [...usage, dailyHistory] as const,
    queryFn: async ({ queryKey: [orgId] }) =>
      await apiClient.billing.getUsage(orgId, dailyHistory ? { history: 'daily' } : undefined),
    enabled: !!orgId
  });
};

export const useGetOrganizationInvoices = () => {
  const apiClient = useApiClient();
  const { invoices } = useCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: invoices,
    queryFn: async ({ queryKey: [orgId] }) => await apiClient.billing.getInvoices(orgId),
    enabled: !!orgId
  });
};

export const useGetManageSubscriptionLink = () => {
  const organizationId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  return useCallback(
    async () => await apiClient.billing.getManageSubscriptionLink(organizationId),
    [apiClient.billing, organizationId]
  );
};
